/**
 * Owner 2026-11-04 (migration 1760): a plain, non-blocking notice on an upcoming event with no lead rep on its Sales
 * Reps list. Start / close of the event day read the list, so without a lead nobody can start or close the day and
 * managers are not alerted when it isn't closed. When the event's Lead / Event Rep field names someone who is not on
 * the list, offer to add that person as the lead in one click (only to users the server lets set the lead).
 */
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import {
  addFieldRepAsLead,
  getEventLeadRepStatus,
  LEAD_STATUS_KEY,
} from "../../services/eventLeadRepStatus.service";

export const NO_LEAD_MESSAGE =
  "This event has no lead rep. Add one under Sales Reps — without a lead, nobody can start or close the event day, and managers won't be alerted if the day isn't closed.";

interface Props {
  eventId: string;
  /** opens the tab with the Sales Reps list (Confirm Schedule) */
  onOpenSalesReps?: () => void;
}

const LeadRepNotice: React.FC<Props> = ({ eventId, onOpenSalesReps }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [adding, setAdding] = useState(false);
  // under the Sales Reps list's own key: every staffing change there (invalidating ["event-sales-assignments", id])
  // refreshes the notice too
  const { data } = useQuery({
    queryKey: ["event-sales-assignments", eventId, LEAD_STATUS_KEY],
    queryFn: () => getEventLeadRepStatus([eventId]),
    enabled: !!eventId,
    staleTime: 30_000,
  });
  const s = data?.[eventId];
  if (!s || !s.needs_lead) {
    return null;
  }
  const fieldName = s.field_name?.trim() || null;
  const showField = !!fieldName && s.field_on_list_as !== "lead";

  const add = async () => {
    if (!s.field_position_id) {
      return;
    }
    setAdding(true);
    try {
      await addFieldRepAsLead(eventId, s.field_position_id);
      showToast(
        "success",
        `${fieldName} is now the lead rep on the Sales Reps list.`,
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["event-sales-assignments", eventId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["event-staffing", eventId],
        }),
        queryClient.invalidateQueries({ queryKey: ["events-missing-lead"] }),
        queryClient.invalidateQueries({ queryKey: [LEAD_STATUS_KEY] }),
      ]);
    } catch (e) {
      showToast(
        "error",
        `${fieldName} was not added: ${(e as Error)?.message ?? "unknown error"}`,
      );
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-yellow-300 bg-yellow-50 p-4"
      role="status"
      data-testid="lead-rep-notice"
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon
          className="h-5 w-5 flex-shrink-0 text-yellow-600"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1 space-y-2 text-sm text-yellow-900">
          <p className="font-medium">{NO_LEAD_MESSAGE}</p>
          {showField && (
            <p data-testid="lead-rep-notice-field">
              Lead / Event Rep is set to {fieldName} but {fieldName} isn't on
              the Sales Reps list
              {s.field_on_list_as === "assigned" ? " as the lead" : ""}.
            </p>
          )}
          {s.list_lead_inactive && s.list_lead_name && (
            <p data-testid="lead-rep-notice-inactive">
              The lead on the Sales Reps list, {s.list_lead_name}, is no longer
              active. Choose another lead.
            </p>
          )}
          {!s.can_set_lead && (
            <p className="text-yellow-800">
              You can't change this event's lead. Ask an admin or the event's
              Regional / Territory Manager to add one.
            </p>
          )}
          {s.can_set_lead && (
            <div className="flex flex-wrap gap-2 pt-1">
              {showField && s.can_add_field_lead && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => void add()}
                  disabled={adding}
                  isLoading={adding}
                  data-testid="lead-rep-notice-add"
                >
                  Add {fieldName} as lead
                </Button>
              )}
              {onOpenSalesReps && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onOpenSalesReps}
                  data-testid="lead-rep-notice-open"
                  title="The Sales Reps list is on the Confirm Schedule tab"
                >
                  Choose a lead on the Confirm Schedule tab
                </Button>
              )}
              {showField && !s.can_add_field_lead && (
                <span className="self-center text-yellow-800">
                  {fieldName} can't be added from here (their seat is inactive
                  or they're outside your team). Choose a lead instead.
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadRepNotice;
